import { useMemo, useState, useEffect, useRef } from "react";
import { format as dateFormat } from "date-fns";
import { ContentCopy, Download } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import { TextField, InputAdornment } from "@mui/material";
import DataTable from "../Table/Datatable";
import { DOMAIN_API_HOST } from "../../../configs/main";
import DateFromTo from "../../../widgets/DateFromTo/DateFromTo";

const STATUS_MAP = {
  INACTIVE: "Неактивна",
  WORK: "Работает",
  MODERATION: "На модерации",
};

const TARGET_TYPE = {
  TRAFFIC: "Трафик",
  REACH: "Охват",
};

const getToken = () => {
  return localStorage.getItem("agency_token");
};

const addOneDay = (date) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + 1);
  return newDate;
};

const fetchAgencyCampaigns = async (params) => {
  const token = getToken();
  const queryParams = new URLSearchParams();

  if (params.page !== undefined) queryParams.append("page", params.page);
  if (params.limit !== undefined) queryParams.append("limit", params.limit);
  if (params.search) queryParams.append("search", params.search);
  if (params.agency) queryParams.append("agency", params.agency);

  const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/campaigns?${queryParams.toString()}`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Ошибка при загрузке кампаний" }));
    throw new Error(errorData.detail || "Ошибка при загрузке кампаний");
  }

  return response.json();
};

const columns = [
  { Header: "ID РК", accessor: "id", width: 130, minWidth: 80, maxWidth: 400 },
  { Header: "Название", accessor: "name", width: 200, minWidth: 80, maxWidth: 400 },
  { Header: "Сайт", accessor: "site", width: 250, minWidth: 80, maxWidth: 400 },
  { Header: "Почта клиента", accessor: "campaign_creator_email", width: 250, minWidth: 80, maxWidth: 400 },
  { Header: "Статистика", accessor: "banners_stats", maxWidth: 400, minWidth: 80, width: 150 },
  { Header: "Дата создания", accessor: "created_at", width: 130, minWidth: 80, maxWidth: 400 },
  { Header: "Тип РК", accessor: "type", width: 117, minWidth: 60, maxWidth: 400 },
  { Header: "Ставка", accessor: "bet", width: 130, minWidth: 80, maxWidth: 400 },
  { Header: "Дневной бюджет", accessor: "daily_budget", width: 145, minWidth: 80, maxWidth: 400 },
  { Header: "Общий бюджет", accessor: "full_budget", width: 145, minWidth: 80, maxWidth: 400 },
  { Header: "Период с-по", accessor: "range", width: 200, minWidth: 80, maxWidth: 400 },
  { Header: "Статус", accessor: "status", width: 200, minWidth: 50, maxWidth: 400 },
];

const BannersStatsModal = ({ openModal, setOpenModal, campaignName, campaignId }) => {
  const [filtersValue, setValue] = useState({
    date: {
      date_start: new Date(new Date().setHours(0, 0, 0, 0) - 1 * 24 * 60 * 60 * 1000),
      date_end: new Date(new Date().setHours(23, 59, 59, 999)),
    },
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [filterArr, setFilter] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const limit = 10;

  useEffect(() => {
    if (openModal && campaignId && filtersValue.date.date_start && filtersValue.date.date_end) {
      const fetchData = async () => {
        setIsLoading(true);
        const token = getToken();
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", `Bearer ${token}`);

        const _startDate = typeof filtersValue.date.date_start === "number" ? new Date(filtersValue.date.date_start) : filtersValue.date.date_start;
        const _endDate = typeof filtersValue.date.date_end === "number" ? new Date(filtersValue.date.date_end) : filtersValue.date.date_end;

        const date_start = new Date(`${_startDate.getFullYear()}.${_startDate.getMonth() + 1}.${_startDate.getDate()} 00:00:00+03:00`).getTime() / 1000;
        const date_end = addOneDay(new Date(`${_endDate.getFullYear()}.${_endDate.getMonth() + 1}.${_endDate.getDate()} 00:00:00+03:00`)).getTime() / 1000;

        try {
          const queryParams = `campaign_id=${campaignId}&date_from=${date_start}&date_to=${date_end}&page=${currentPage}&limit=${limit}`;
          const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/statistics?${queryParams}`, {
            method: "GET",
            headers: myHeaders,
          });

          if (response.ok) {
            const data = await response.json();
            setFilter(data.data || []);
            setTotalPages(data.total_pages || 1);
          } else {
            console.error("Ошибка при загрузке данных:", response.statusText);
            setFilter([]);
          }
        } catch (error) {
          console.error("Ошибка при загрузке данных:", error);
          setFilter([]);
        } finally {
          setIsLoading(false);
        }
      };

      fetchData();
    }
  }, [openModal, campaignId, filtersValue.date.date_start, filtersValue.date.date_end, currentPage]);

  const paginatedData = filterArr.slice((currentPage - 1) * limit, currentPage * limit);
  const displayTotalPages = totalPages;

  const financeColumns = [
    {
      Header: "Дата",
      accessor: "date",
      maxWidth: 400,
      minWidth: 80,
      width: 200,
    },
    {
      Header: "Показы",
      accessor: "views1",
      maxWidth: 400,
      minWidth: 80,
      width: 150,
    },
    {
      Header: "Клики",
      accessor: "clicks",
      maxWidth: 400,
      minWidth: 80,
      width: 150,
    },
    {
      Header: "Расход",
      accessor: "spent",
      maxWidth: 400,
      minWidth: 80,
      width: 150,
    },
    // {
    //   Header: "CTR",
    //   accessor: "ctr",
    //   maxWidth: 400,
    //   minWidth: 80,
    //   width: 150,
    // },
    // {
    //   Header: "eCPM",
    //   accessor: "ecpm",
    //   maxWidth: 400,
    //   minWidth: 80,
    //   width: 150,
    // },
    // {
    //   Header: "Затраты",
    //   accessor: "total_cost",
    //   maxWidth: 400,
    //   minWidth: 80,
    //   width: 150,
    // },
  ];

  const getTableData = () => {
    return paginatedData.map((el) => ({
      date: <div className="td">{el.date ? dateFormat(new Date(el.date), "dd.MM.yyyy") : "-"}</div>,
      views1: <div className="td">{(el.views ?? el.impressions ?? 0).toLocaleString("ru-RU")}</div>,
      clicks: <div className="td">{el.clicks ? el.clicks.toLocaleString("ru-RU") : "0"}</div>,
      spent: <div className="td">{(el.spent ?? el.total_cost ?? 0).toLocaleString("ru-RU")} ₽</div>,
      // ctr: <div className="td">{el.ctr ? Number(el.ctr).toFixed(2) + "%" : "0%"}</div>,
      // ecpm: <div className="td">{el.ecpm ? el.ecpm.toFixed(2) : "0"}</div>,
      // total_cost: <div className="td">{el.total_cost ? el.total_cost.toLocaleString("ru-RU") + " ₽" : "0 ₽"}</div>,
    }));
  };

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    } else if (direction === "next" && currentPage < displayTotalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (openModal) {
      setCurrentPage(1);
    }
  }, [openModal]);

  const handleDateChange = (value) => {
    setValue((prev) => ({
      ...prev,
      date: { date_end: value[1] ?? prev.date.date_end, date_start: value[0] ?? prev.date.date_start },
    }));
  };

  const handleDownload = async () => {
    if (!campaignId) {
      console.error("campaignId отсутствует:", campaignId);
      return;
    }

    const token = getToken();
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const _startDate = typeof filtersValue.date.date_start === "number" ? new Date(filtersValue.date.date_start) : filtersValue.date.date_start;
    const _endDate = typeof filtersValue.date.date_end === "number" ? new Date(filtersValue.date.date_end) : filtersValue.date.date_end;

    const date_start = new Date(`${_startDate.getFullYear()}.${_startDate.getMonth() + 1}.${_startDate.getDate()} 00:00:00+03:00`).getTime() / 1000;
    const date_end = addOneDay(new Date(`${_endDate.getFullYear()}.${_endDate.getMonth() + 1}.${_endDate.getDate()} 00:00:00+03:00`)).getTime() / 1000;

    const queryParams = new URLSearchParams({
      campaign_id: campaignId.toString(),
      date_from: date_start.toString(),
      date_to: date_end.toString(),
    });

    try {
      const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/statistics/export?${queryParams.toString()}`, {
        method: "GET",
        headers: myHeaders,
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers.get("content-disposition");
        let filename = `statistics_${campaignName || "campaign"}.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Ошибка при скачивании файла:", response.statusText);
      }
    } catch (error) {
      console.error("Ошибка при скачивании файла:", error);
    }
  };

  if (!openModal) return null;

  const modalStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  };

  const modalContentStyles = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    width: "90%",
    maxWidth: "720px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  };

  const headerStyles = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  };

  const titleStyles = {
    fontSize: "20px",
    fontWeight: 600,
    color: "#1a1a1a",
    margin: 0,
  };

  const closeButtonStyles = {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#666",
    padding: 0,
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s",
  };

  const downloadButtonStyles = {
    backgroundColor: "#3f73f9",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    whiteSpace: "nowrap",
    boxShadow: "0 2px 8px rgba(63, 115, 249, 0.3)",
  };

  return (
    <div style={modalStyles} onClick={() => setOpenModal(false)}>
      <div style={modalContentStyles} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyles}>
          <h3 style={titleStyles}>
            Статистика по кампании - {campaignName || ""}
          </h3>
          <button
            style={closeButtonStyles}
            onClick={() => setOpenModal(false)}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#f5f5f5";
              e.target.style.color = "#333";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "#666";
            }}
          >
            ×
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#333" }}>
              Выберите период
            </label>
            <div style={{ width: "100%", marginBottom: "10px" }}>
              <DateFromTo
                from={filtersValue.date.date_start}
                to={filtersValue.date.date_end}
                handleChange={handleDateChange}
              />
            </div>
            <button
              style={downloadButtonStyles}
              onClick={handleDownload}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#2d5cd8";
                e.target.style.boxShadow = "0 4px 12px rgba(63, 115, 249, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#3f73f9";
                e.target.style.boxShadow = "0 2px 8px rgba(63, 115, 249, 0.3)";
              }}
            >
              <Download fontSize="small" />
              Скачать отчёт
            </button>
          </div>
          <div style={{ marginTop: "10px", overflowX: "auto" }}>
            {isLoading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px" }}>
                <p>Загрузка данных...</p>
              </div>
            ) : paginatedData.length === 0 ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px", color: "rgba(0,0,0,0.6)" }}>
                Данные не найдены
              </div>
            ) : (
              <DataTable data={getTableData()} columns={financeColumns} />
            )}
          </div>
          {displayTotalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", marginTop: "20px" }}>
              <button
                style={{
                  padding: "6px 16px",
                  borderRadius: 6,
                  border: "none",
                  background: currentPage === 1 ? "#e0e0e0" : "#dfe8ff",
                  color: currentPage === 1 ? "#888" : "#3f73f9",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
                onClick={() => handlePageChange("prev")}
                disabled={currentPage === 1}
              >
                Назад
              </button>
              <strong style={{ fontSize: "14px" }}>
                {currentPage} / {displayTotalPages}
              </strong>
              <button
                style={{
                  padding: "6px 16px",
                  borderRadius: 6,
                  border: "none",
                  background: currentPage === displayTotalPages ? "#e0e0e0" : "#dfe8ff",
                  color: currentPage === displayTotalPages ? "#888" : "#3f73f9",
                  cursor: currentPage === displayTotalPages ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
                onClick={() => handlePageChange("next")}
                disabled={currentPage === displayTotalPages}
              >
                Дальше
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function AgencyCampaigns() {
  const { enqueueSnackbar } = useSnackbar();
  const ITEMS_PER_PAGE = 100;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const searchDebounceTimer = useRef(null);
  const [openModal, setOpenModal] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  useEffect(() => {
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 600);

    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    const loadCampaigns = async () => {
      setIsLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: ITEMS_PER_PAGE,
        };

        if (debouncedSearchQuery.trim()) {
          params.search = debouncedSearchQuery.trim();
        }

        const response = await fetchAgencyCampaigns(params);
        const data = Array.isArray(response.data) ? response.data : [];

        setCampaigns(data);
        setTotalPages(Math.max(1, response.total_pages || 1));
      } catch (error) {
        enqueueSnackbar(error.message || "Ошибка при загрузке кампаний", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        setCampaigns([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCampaigns();
  }, [currentPage, debouncedSearchQuery, enqueueSnackbar]);

  const tableData = useMemo(
    () =>
      campaigns.map((campaign) => ({
        id: (
          <div className="td" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {String(campaign.id).slice(0, 8)}
            <ContentCopy
              fontSize="small"
              className="copyIcon"
              style={{ cursor: "pointer" }}
              onClick={() => {
                navigator.clipboard.writeText(String(campaign.id));
                enqueueSnackbar("ID кампании скопирован", {
                  variant: "success",
                  autoHideDuration: 1000,
                  anchorOrigin: { vertical: "top", horizontal: "right" },
                });
              }}
            />
          </div>
        ),
        name: <div className="td">{campaign.name || "-"}</div>,
        site: <div className="td">{campaign.site || "-"}</div>,
        campaign_creator_email: <div className="td">{campaign.campaign_creator_email || "-"}</div>,
        created_at: (
          <div className="td">
            {campaign.created_at ? dateFormat(new Date(campaign.created_at), "dd/MM/yyyy") : "-"}
          </div>
        ),
        type: <div className="td">{TARGET_TYPE[campaign.target_type] || "-"}</div>,
        daily_budget: <div className="td">{(campaign.daily_budget || 0).toLocaleString("ru-RU")} ₽</div>,
        full_budget: <div className="td">{(campaign.full_budget || 0).toLocaleString("ru-RU")} ₽</div>,
        bet: <div className="td">{(campaign.price_per_action || 0).toLocaleString("ru-RU")} ₽</div>,
        range: (
          <div className="td">
            {campaign.validity_period?.date_start && campaign.validity_period?.date_end
              ? `${dateFormat(new Date(campaign.validity_period.date_start), "dd/MM/yyyy")} - ${dateFormat(new Date(campaign.validity_period.date_end), "dd/MM/yyyy")}`
              : "-"}
          </div>
        ),
        status: <div className="td">{STATUS_MAP[campaign.status] || campaign.status || "-"}</div>,
        banners_stats: (
          <button
            onClick={() => {
              setCampaignName(campaign.name || "");
              setSelectedCampaignId(campaign.id);
              setOpenModal(true);
            }}
            style={{
              padding: "5px 10px",
              fontSize: "12px",
              fontWeight: "500",
              borderRadius: 5,
              background: "#DFE8FF",
              color: "#3F73F9",
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#C5D5FF";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#DFE8FF";
            }}
          >
            Открыть
          </button>
        ),
      })),
    [campaigns, enqueueSnackbar]
  );

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  return (
    <div style={{ width: "100%", padding: "30px", height: "95%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2>Кампании клиентов</h2>
        <TextField
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Поиск по ID, email, телефону или имени"
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div style={{ width: "100%", overflow: "auto", height: "80%" }}>
        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
            <p>Загрузка...</p>
          </div>
        ) : campaigns.length === 0 ? (
          <div
            style={{
              minHeight: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(0,0,0,0.6)",
              fontSize: 16,
            }}
          >
            {debouncedSearchQuery ? "Кампании не найдены" : "Кампаний пока нет"}
          </div>
        ) : (
          <DataTable data={tableData} columns={columns} />
        )}
      </div>
      {!isLoading && campaigns.length > 0 && totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16 }}>
          <button
            style={{
              padding: "6px 16px",
              borderRadius: 6,
              border: "none",
              background: currentPage === 1 ? "#e0e0e0" : "#dfe8ff",
              color: currentPage === 1 ? "#888" : "#3f73f9",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
            onClick={() => handlePageChange("prev")}
            disabled={currentPage === 1}
          >
            Назад
          </button>
          <strong style={{ marginTop: 6 }}>
            {currentPage} / {totalPages}
          </strong>
          <button
            style={{
              padding: "6px 16px",
              borderRadius: 6,
              border: "none",
              background: currentPage === totalPages ? "#e0e0e0" : "#dfe8ff",
              color: currentPage === totalPages ? "#888" : "#3f73f9",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
            onClick={() => handlePageChange("next")}
            disabled={currentPage === totalPages}
          >
            Дальше
          </button>
        </div>
      )}
      <BannersStatsModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        campaignName={campaignName}
        campaignId={selectedCampaignId}
      />
    </div>
  );
}

export default AgencyCampaigns;

